import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Archive, CheckCircle2, Download, HardDrive, Loader2, RefreshCw, ShieldCheck, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useCompany } from '../context/CompanyContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b === 0 ? '0 KB' : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDate = (s) => new Date(s).toLocaleString();
const KIND = { client: 'Client', system: 'System (nightly)', prerestore: 'Safety copy' };

async function call(path, options = {}) {
  const res = await fetch(`${API_URL}/api/backup${path}`, { credentials: 'include', ...options });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

async function saveToComputer(name) {
  const res = await fetch(`${API_URL}/api/backup/download/${encodeURIComponent(name)}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Download failed');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

const BackupRestore = () => {
  const [list, setList] = useState(null);
  const { companies: clients, selectedCompany } = useCompany();
  const [clientId, setClientId] = useState(selectedCompany?.id || 'default');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [alsoDownload, setAlsoDownload] = useState(true);
  const [busy, setBusy] = useState(false);
  const [restore, setRestore] = useState(null); // {source: {server_name}|{file}, label}
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const r = await call('/list');
    if (r.ok) setList(r.data);
    else toast.error(r.data.detail || 'Could not load backups');
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const backupNow = async () => {
    if (password !== password2) { toast.error('The two passwords do not match'); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append('scope', `client:${clientId}`);
    fd.append('password', password);
    const r = await call('/create', { method: 'POST', body: fd });
    setBusy(false);
    if (!r.ok) { toast.error(typeof r.data.detail === 'string' ? r.data.detail : 'Backup failed'); return; }
    toast.success(`Backup created: ${r.data.total_documents} records (${fmtSize(r.data.size)}).`);
    setPassword(''); setPassword2('');
    if (alsoDownload) {
      try { await saveToComputer(r.data.name); } catch (e) { toast.error('Backup saved on the server, but the download failed. Use Download in the list.'); }
    }
    load();
  };

  const remove = async (name) => {
    if (!window.confirm(`Delete ${name} from the server? This cannot be undone.`)) return;
    const r = await call(`/${encodeURIComponent(name)}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Backup deleted'); load(); } else toast.error(r.data.detail || 'Could not delete');
  };

  const nightly = list?.last_system_backup;
  const totalSize = (list?.files || []).reduce((s, f) => s + f.size, 0);

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Backup &amp; Restore</h1>
        <p className="text-gray-500 mt-1">Encrypted backups of each client&apos;s books, kept on the server and downloadable to your own computer.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card data-testid="nightly-status" className={list && !list.system_backup_healthy ? 'border-amber-300' : 'border-emerald-300'}>
          <CardContent className="p-4">
            {list?.system_backup_healthy ? <CheckCircle2 className="w-7 h-7 text-emerald-500 mb-2" /> : <AlertTriangle className="w-7 h-7 text-amber-500 mb-2" />}
            <p className="font-semibold text-gray-900">
              {nightly ? `Nightly system backup ${list.system_backup_healthy ? 'is running' : 'is overdue'}` : 'No nightly system backup found'}
            </p>
            <p className="text-sm text-gray-500">
              {nightly ? `Last one ${fmtDate(nightly.created_at)} (${list.last_system_backup_age_hours} h ago)` : 'The server has not produced a whole-system backup yet.'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <Archive className="w-7 h-7 text-sky-500 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{list ? list.files.length : '–'}</p>
            <p className="text-sm text-gray-500">Backups on the server</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <HardDrive className="w-7 h-7 text-purple-500 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{fmtSize(totalSize)}</p>
            <p className="text-sm text-gray-500">Storage used</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Back up a client</CardTitle>
          <CardDescription>
            Choose a password. Backups contain payroll data and SSNs, so they are always encrypted.{' '}
            <b>The password is not stored anywhere: if it is lost, the backup cannot be opened.</b>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <label className="text-sm text-gray-600 flex flex-col gap-1">Client
              <select className="border rounded-md px-3 py-2 bg-white" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="backup-client">
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="text-sm text-gray-600 flex flex-col gap-1">Password (min {list?.min_password_length || 8} characters)
              <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="backup-password" />
            </label>
            <label className="text-sm text-gray-600 flex flex-col gap-1">Repeat password
              <Input type="password" autoComplete="new-password" value={password2} onChange={(e) => setPassword2(e.target.value)} data-testid="backup-password2" />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={alsoDownload} onChange={(e) => setAlsoDownload(e.target.checked)} />
            Also download a copy to this computer
          </label>
          <Button onClick={backupNow} disabled={busy || password.length < (list?.min_password_length || 8) || password !== password2} data-testid="backup-now-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}Back up now
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Backups</CardTitle>
            <CardDescription>Client backups you create stay until you delete them. Nightly system backups are kept for 30 days.</CardDescription>
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".bsbackup" className="hidden" data-testid="restore-file"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setRestore({ source: { file: f }, label: f.name }); e.target.value = ''; }} />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="restore-from-computer-btn"><Upload className="w-4 h-4 mr-2" />Restore from a file…</Button>
            <Button variant="ghost" size="icon" onClick={load} aria-label="Refresh"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardHeader>
        <CardContent>
          {!list ? <Loader2 className="w-5 h-5 animate-spin text-sky-600" /> : list.files.length === 0 ? (
            <p className="text-sm text-gray-500">No backups yet. Create the first one above.</p>
          ) : (
            <div className="divide-y" data-testid="backup-list">
              {list.files.map((f) => (
                <div key={f.name} className="py-3 flex flex-wrap items-center gap-3" data-testid={`backup-row-${f.name}`}>
                  <Archive className="w-5 h-5 text-emerald-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-sm text-gray-900 truncate">{f.name}</p>
                    <p className="text-xs text-gray-500">{fmtDate(f.created_at)} · {fmtSize(f.size)}{f.company_id ? ` · client ${f.company_id}` : ' · all clients'}</p>
                  </div>
                  <Badge variant="outline">{KIND[f.kind] || f.kind}</Badge>
                  <Button variant="outline" size="sm" onClick={() => saveToComputer(f.name).catch(() => toast.error('Download failed'))}><Download className="w-4 h-4 mr-1" />Download</Button>
                  {f.kind !== 'system' && (
                    <Button variant="outline" size="sm" onClick={() => setRestore({ source: { server_name: f.name }, label: f.name })} data-testid={`restore-${f.name}`}><RefreshCw className="w-4 h-4 mr-1" />Restore</Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(f.name)} aria-label="Delete"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-4">
            Whole-system (nightly) backups are restored on the server with <code>scripts/restore_backup.py</code>, not from this screen.
          </p>
        </CardContent>
      </Card>

      {restore && <RestoreDialog target={restore} onClose={() => setRestore(null)} onDone={() => { setRestore(null); load(); }} />}
    </>
  );
};

const RestoreDialog = ({ target, onClose, onDone }) => {
  const [password, setPassword] = useState('');
  const [info, setInfo] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const form = (extra = {}) => {
    const fd = new FormData();
    fd.append('password', password);
    if (target.source.file) fd.append('file', target.source.file, target.source.file.name);
    if (target.source.server_name) fd.append('server_name', target.source.server_name);
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    return fd;
  };

  const check = async () => {
    setBusy(true); setError('');
    const r = await call('/inspect', { method: 'POST', body: form() });
    setBusy(false);
    if (!r.ok) { setError(typeof r.data.detail === 'string' ? r.data.detail : 'Could not open the backup'); return; }
    setInfo(r.data);
  };

  const doRestore = async () => {
    setBusy(true); setError('');
    const r = await call('/restore', { method: 'POST', body: form({ confirm_text: confirmText }) });
    setBusy(false);
    if (!r.ok) { setError(typeof r.data.detail === 'string' ? r.data.detail : 'Restore failed'); return; }
    setResult(r.data);
    toast.success(`Restored ${r.data.total_documents} records.`);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) (result ? onDone() : onClose()); }}>
      <DialogContent className="max-w-lg" data-testid="restore-dialog">
        <DialogHeader>
          <DialogTitle>Restore from a backup</DialogTitle>
          <DialogDescription className="font-mono break-all">{target.label}</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-2 text-sm" data-testid="restore-result">
            <p className="text-emerald-700 font-medium"><CheckCircle2 className="w-4 h-4 inline mr-1" />Restored {result.total_documents} records for {result.scope.replace('client:', 'client ')}.</p>
            <p className="text-gray-600">The data as it was just before the restore is saved as <span className="font-mono">{result.safety_backup}</span> (same password). If this was a mistake, restore that file.</p>
          </div>
        ) : !info ? (
          <div className="space-y-3">
            <label className="text-sm text-gray-600 flex flex-col gap-1">Backup password
              <Input type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="restore-password"
                onKeyDown={(e) => { if (e.key === 'Enter' && password) check(); }} />
            </label>
          </div>
        ) : (
          <div className="space-y-3 text-sm" data-testid="restore-preview">
            <p>Backup of <b>{info.scope.replace('client:', 'client ')}</b> made {fmtDate(info.created_at)} by {info.created_by}. The file is intact (all checksums verified).</p>
            <div className="rounded border bg-slate-50 p-2 max-h-36 overflow-auto">
              {Object.entries(info.collections).map(([k, n]) => <div key={k} className="flex justify-between"><span>{k}</span><span>{n}</span></div>)}
              <div className="flex justify-between font-semibold border-t mt-1 pt-1"><span>Total</span><span>{info.total_documents}</span></div>
            </div>
            {info.restorable_here ? (
              <>
                <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  This replaces the {info.current_documents_that_would_be_replaced} record(s) this client has now with the {info.total_documents} in the backup.
                  Anything entered since the backup is removed. Other clients are not touched. A safety copy of the current data is saved first.
                </p>
                <label className="text-gray-600 flex flex-col gap-1">Type RESTORE to confirm
                  <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="restore-confirm-text" />
                </label>
              </>
            ) : (
              <p className="text-red-700">This is a whole-system backup. Restore it on the server with <code>scripts/restore_backup.py</code>.</p>
            )}
          </div>
        )}

        {error && <p className="text-sm text-red-700" data-testid="restore-error">{error}</p>}
        <DialogFooter>
          {result ? <Button onClick={onDone}>Close</Button> : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              {!info ? (
                <Button onClick={check} disabled={busy || !password} data-testid="restore-check-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Check backup</Button>
              ) : info.restorable_here && (
                <Button className="bg-red-600 hover:bg-red-700" onClick={doRestore} disabled={busy || confirmText.trim() !== 'RESTORE'} data-testid="restore-btn">
                  {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Restore now
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BackupRestore;
